import React, {useContext, useRef, useState} from "react";
import { observer } from "mobx-react-lite";
import { AppStoreContext } from "../../stores/AppStore";
import { ResumeStoreContext } from "../../stores/ResumeStore";
import {
  Button,
  Header,
  Responsive,
  Form,
  Segment,
  Modal,
  Container
} from "semantic-ui-react";
import "./Resume.css";
import { useTranslation } from "react-i18next";
import ResumeUploadView from "./ResumeUploadView";
import LinkedInView from "./LinkedInView";
import { ROUTES } from "../../stores/routes";
import GLGIcon from "../GLGIcon";
import { trackAction } from "../../tracking";
import OnboardingActions from "../../tracking/actions";
import { GLOBAL_MODALS } from "../../stores/modals";
import { RateStoreContext } from "../../stores/RateStore";
import { post } from "../../lib/http";
import ProfileView, { profileIframeRef } from "./ProfileView";

// Styles
const successIcon = {
  border: "solid 1px white",
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  padding: "4px",
  background: "white",
  color: "#8bc344"
};

const requestProfileSave = async (action, params = {}) => new Promise(resolve => {
  let _resolved = false;
  const moveOn = () => {
    if (!_resolved) {
      _resolved = true;
      window.removeEventListener("message", onMessage);
      setTimeout(() => resolve(), 0); // wait a tick for all messaged to be delivered
    }
  }
  const onMessage = (e) => (e.data.action === action + 'Done') && moveOn();
  window.addEventListener("message", onMessage);
  profileIframeRef.current.contentWindow.postMessage({ ...params, action }, "*");
  
  // escape hatch in case we don't get the callback above
  setTimeout(moveOn, 3000);
});


export default observer(function Resume() {
  // Init values
  const appStore = useContext(AppStoreContext);
  const rateStore = useContext(RateStoreContext);
  const resumeStore = useContext(ResumeStoreContext);
  const [nextClicked, setNextClicked] = useState(false);
  const profileRef = useRef();

  const [t] = useTranslation();

  const advanceNext = () => {
    trackAction(OnboardingActions.global.Next);
    if (nextClicked) {
      return;
    }
    setNextClicked(true);
    // we post on user clicks next instead of when they update linkedin
    // because update linkedin can run multiple times, specially as CM types and re-types
    post("api/queueLinkedInForProcessing")
      .catch(() => true)
      .then(() => {
        if (rateStore.rateAmountOriginal === 0) {
          appStore.currentRoute = ROUTES.RATE;
        } else if (!!appStore.hasTNC && !appStore.isProfileProcessing) {
          // already T&C'd, just go to redirect
          appStore.goToRedirector();
        } else if (!appStore.reminderDateUTC || new Date(appStore.reminderDateUTC) < new Date()) {
          appStore.globalModal = GLOBAL_MODALS.WHATS_NEXT;
        } else {
          appStore.currentRoute = ROUTES.THANK_YOU;
        }
      })
  }

  const handleNextButton = async (e) => {
    e.preventDefault();
    if (nextClicked) {
      return;
    }
    setNextClicked(true);
    
    //------------------------ with profile iframe
    if (profileRef.current) {
      
      // has highlights (not required or non-en, or leads)
      await requestProfileSave('saveHighlights');
      const highlights = profileRef.current.getHighlights();
      if (profileRef.current.getSectionsInEdit().indexOf('nm-highlights') !== -1 || (highlights || '').trim() === '') {
        profileIframeRef.current.contentWindow.postMessage({ action: "requestScrollToSection", section: 'nm-highlights' }, "*");
        return setNextClicked(false);
      }

      // has jobs or linkedin
      await requestProfileSave('saveWorkHistory', { cancelIfNotDirty: !!resumeStore.linkedInProfileUrl });
      if (profileRef.current.getSectionsInEdit().length > 0) {
        if (!resumeStore.linkedInProfileUrl) {
          window.GLG.Cmx.User.Show.error('Please enter at least one job or add your linkedin public profile url');
        }
        profileIframeRef.current.contentWindow.postMessage({ action: "requestScrollToSection", section: profileRef.current.getSectionsInEdit()[0] }, "*");
        return setNextClicked(false);
      }
      const jobsCount = profileRef.current.getJobsCount();
      if ((jobsCount === 0) && (!resumeStore.linkedInProfileUrl)) {
        window.GLG.Cmx.User.Show.error('Please enter at least one job or add your linkedin public profile url');
        profileIframeRef.current.contentWindow.postMessage({ action: "requestScrollToSection", section: 'work-history' }, "*");
        return setNextClicked(false);
      }

      setNextClicked(false);
      advanceNext();
      return;
    }
    
    //------------------------ without profile iframe
    setNextClicked(false);
    if (
      !resumeStore.linkedInProfileUrl &&
      !resumeStore.resumeReceivedUTC &&
      !resumeStore.resumeURL &&
      !(appStore.zoneRegion && appStore.zoneRegion === "Asia")
    ) {
      trackAction(OnboardingActions.ManualEntryDisplayed);
      resumeStore.showModal = true;
    } else {
      advanceNext();
    }
  };

  const getConfirmationCopy = () => {	
    if (!!resumeStore.linkedInProfileUrl){
      return t("save_complete")
    } else {
      return resumeStore.resumeURL ? t("save_complete") : t("upload_complete");	
    }	
  }

  // Render Components..
  return (
    <>
      <div className="view view-container" style={{ paddingBottom: "14px" }}>
        <Header as="h2">{t("resume_title")}</Header>
        {resumeStore.showConfirmationStatusBar ? (
          <Segment
            color={"green"}
            compact
            inverted
            style={{ display: "flex", justifyContent: "space-between" }}
            textAlign={"center"}
          >
            <div>
              <GLGIcon
                name={"check-4-1"}
                size={"small"}
                circular
                style={successIcon}
              />{" "}
              {getConfirmationCopy()}
            </div>
            <GLGIcon
              name={"close"}
              onClick={() => (resumeStore.showConfirmationStatusBar = false)}
            />
          </Segment>
        ) : (
          <p style={{padding: "0 2rem"}}>{t("resume_sub_heading")}</p>
        )}

        {/^en/i.test(appStore.locale || 'en') && !appStore.recruitedBy && appStore.recruitedBy !== 0 && <ProfileView componentRef={profileRef} />}

        <LinkedInView  />

        <ResumeUploadView />

        <>
          <Responsive maxWidth={400}>
            <Form.Button
              primary
              fluid
              disabled={resumeStore.isLoading || resumeStore.isBusy}
              style={{ margin: "20px 0 20px 0" }}
              loading={resumeStore.isLoading || resumeStore.isBusy}
              onClick={handleNextButton}
            >
              {t("next")}
            </Form.Button>
          </Responsive>
          <Responsive minWidth={401}>
            <Form.Button
              primary
              fluid={false}
              disabled={resumeStore.isLoading || resumeStore.isBusy}
              style={{ margin: "20px 0 20px 0" }}
              floated="right"
              loading={resumeStore.isLoading || resumeStore.isBusy || nextClicked}
              onClick={handleNextButton}
            >
              {t("next")}
            </Form.Button>
          </Responsive>
        </>

        {resumeStore.showModal && (
          <Modal
            dimmer="blurring"
            closeOnEscape={false}
            closeOnDimmerClick={false}
            open={resumeStore.showModal}
            onClose={() => {
              resumeStore.showModal = false;
            }}
            size="small"
            className={"data-notice-modal"}
          >
            <Container fluid textAlign="center" >
              <p dangerouslySetInnerHTML={{ __html: t("no_data_notice") }} />
              <Button
                inverted
                style={{color:"#5890DC", border:"solid 1px #5890DC", marginRight:"10px"}}
                onClick={() => resumeStore.showModal = false }
              >{t("cancel")}</Button>
              <Button
                style={{marginTop:"1rem"}}
                primary
                loading={nextClicked}
                onClick={() => {
                  resumeStore.showModal = false;
                  advanceNext();
                }}
              >{t("continue")}</Button>
            </Container>
          </Modal>
        )}
      </div>
    </>
  );
});
